import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { title } from "@/lib/format";

export function SendToCrmDialog({ open, onClose, leadIds, onSent }) {
  const [integrationId, setIntegrationId] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [result, setResult] = useState(null);
  const integrations = useQuery({
    queryKey: ["crm-integrations"],
    queryFn: () => get("/crm-integrations"),
    enabled: open,
  });
  const choices = (integrations.data || []).filter((row) => row.enabled !== false);
  const selected = choices.find((row) => row.id === integrationId);
  const assignees = selected?.assignees || [];

  useEffect(() => {
    if (!open) return;
    setIntegrationId("");
    setAssigneeId("");
    setResult(null);
  }, [open]);

  const push = useMutation({
    mutationFn: () => api.post("/leads/push-crm", {
      integration_id: integrationId,
      lead_ids: leadIds,
      assignee_id: assignees.length ? assigneeId : undefined,
    }),
    onSuccess: (res) => {
      const data = res.data;
      setResult(data);
      onSent?.(data);
      if (!data.failed) {
        toast.success(`${data.sent} lead${data.sent === 1 ? "" : "s"} sent to CRM`);
        onClose();
      } else {
        toast.error(`${data.sent} sent, ${data.failed} failed`);
      }
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const canSend = Boolean(integrationId) && (!assignees.length || assigneeId) && leadIds.length > 0;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="bg-white">
        <DialogHeader>
          <DialogTitle>Send to CRM</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600" data-testid="crm-send-count">
          {leadIds.length} qualified lead{leadIds.length === 1 ? "" : "s"} will be sent. The CRM uses the assignee to route them to a supervisor or agent.
        </p>
        {integrations.isLoading ? <p className="text-sm text-slate-500">Loading CRM integrations…</p> : null}
        {!integrations.isLoading && choices.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="crm-send-empty">
            No CRM integration is enabled. An admin can add one under CRM.
          </p>
        ) : null}
        {choices.length ? (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>CRM integration</Label>
              <Select value={integrationId || undefined} onValueChange={(value) => { setIntegrationId(value); setAssigneeId(""); setResult(null); }}>
                <SelectTrigger data-testid="crm-send-integration"><SelectValue placeholder="Choose integration" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {choices.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {assignees.length ? (
              <div className="space-y-1.5">
                <Label>Assign to</Label>
                <Select value={assigneeId || undefined} onValueChange={setAssigneeId}>
                  <SelectTrigger data-testid="crm-send-assignee"><SelectValue placeholder="Supervisor or agent" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {assignees.map((row) => (
                      <SelectItem key={row.id} value={row.id}>{row.label} · {title(row.role)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
        ) : null}
        {result?.failed ? (
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-800" data-testid="crm-send-errors">
            {result.results.filter((row) => !row.ok).map((row) => (
              <p key={row.lead_id}>{row.lead_id.slice(0, 8)} · {row.error || `HTTP ${row.status || "error"}`}</p>
            ))}
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!canSend || push.isPending}
            onClick={() => push.mutate()}
            data-testid="crm-send-confirm"
          >
            {push.isPending ? "Sending…" : "Send"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
